import { IResourceSchedule, ITimeInterval } from "../../interfaces/roster/IRoster"
import moment from "moment"
import { EpochConstants } from "../../utils/helpers"
import { forEach } from "lodash"
import { WEEKDAYS } from "../../constants"

export const ROSTER_MESSAGES = {
	CONFLICTING_ROSTER: "Current roster conflicts with existing shifts on ",
	CONFLICTINF_SHIFTS_IN_DAY: "You have added conflicting shifts.",
	INVALID_INTERVAL: "Shift end time should not be less than start time.",
	NULL_SHIFTS: "Please select time for all the shifts.",
	SAME_TIME: "Shift start and end time should not be same.",
	LATE_SHIFT: "Selected working hours fall late in the night.. Please proceed only if you are certain that you will join the consultations.",
	NON_OPERATIONAL_HOURS: "The selected time is not within operational hours (7AM - 11PM).",
}

export const getEpochFromDateAndTime = (epochInMs: number, timeInHHMM: string): number => {
	const [hour, min] = timeInHHMM.split(":")
	return new Date(epochInMs).setHours(Number(hour), Number(min), 0, 0)
}

export const isOverLappingShiftsPresent = (date: number, shifts: ITimeInterval[]) => {
	const intervalsInEpoch = shifts.map(({ startTime, endTime }) => {
		return {
			startTime: getEpochFromDateAndTime(date, startTime as string),
			endTime: getEpochFromDateAndTime(date, endTime as string),
		}
	})
	intervalsInEpoch.sort((a, b) => a.startTime - b.startTime)
	for (let count = 0; count < intervalsInEpoch.length; count++) {
		if (intervalsInEpoch[count + 1]) {
			const startEpochOfExisitingShift = intervalsInEpoch[count].startTime
			const endEpochOfExisitingShift = intervalsInEpoch[count].endTime
			const startEpochOfShiftToCheck = intervalsInEpoch[count + 1].startTime
			const endEpochOfShiftToCheck = intervalsInEpoch[count + 1].endTime
			if (
				(startEpochOfShiftToCheck >= startEpochOfExisitingShift && startEpochOfShiftToCheck < endEpochOfExisitingShift) ||
				(endEpochOfShiftToCheck > startEpochOfExisitingShift && endEpochOfShiftToCheck <= endEpochOfExisitingShift)
			)
				return true
		}
	}
	return false
}

export const isRosterValid = (date: number, shifts: ITimeInterval[]): string => {
	for (let shiftCount = 0; shiftCount < shifts.length; shiftCount++) {
		const startTime: number = getEpochFromDateAndTime(date, shifts[shiftCount].startTime as string)
		const endTime: number = getEpochFromDateAndTime(date, shifts[shiftCount].endTime as string)
		if (endTime < startTime) {
			return ROSTER_MESSAGES.INVALID_INTERVAL
		}
		if (startTime === endTime) {
			return ROSTER_MESSAGES.SAME_TIME
		}
	}
	return null
}

export const isLateNightShift = (shifts: ITimeInterval[]) => {
	const preferredStartTime = moment("4:00a", "HH:mm a")
	const preferredEndTime = moment("11:00p", "HH:mm a")
	for (let shiftCount = 0; shiftCount < shifts.length; shiftCount++) {
		if (shifts[shiftCount].startTime && shifts[shiftCount].endTime) {
			if (
				!(
					moment(shifts[shiftCount].startTime, "HH:mm").isBetween(preferredStartTime, preferredEndTime, null, "[]") &&
					moment(shifts[shiftCount].endTime, "HH:mm").isBetween(preferredStartTime, preferredEndTime, null, "[]")
				)
			)
				return true
		}
	}
	return false
}

export const isAllShiftPresent = (workingDayDate: number, shifts: ITimeInterval[]): boolean => {
	for (let shiftCount = 0; shiftCount < shifts.length; shiftCount++) {
		if (!shifts[shiftCount].startTime) {
			return false
		}
		if (!shifts[shiftCount].endTime) {
			return false
		}
	}
	return true
}

export enum ROSTER_UPDATE_ACTIONS {
	CONFIRM = "CONFIRM",
	PREVIEW = "PREVIEW",
}

export const RESOURCE_TYPE_FOR_ROSTER = "DOCTOR"

export const NumberOfCalendarDays = 20
export const NumberOfCalendarDaysEditable = 14
export const dateFormat = "DD-MM-YYYY"

export interface ICopyRosterSelectedState {
	selectedShifts: ITimeInterval[]
	selectedDates: string[]
}

export const getDateRosterMap = (numberOfCalendarDays: number) => {
	const dateRosterMap: { [date: string]: IResourceSchedule[] } = {}
	for (let dayCount = 0; dayCount <= numberOfCalendarDays; dayCount++) {
		const rangeStartDate = moment(Date.now()).startOf("isoWeek").valueOf()
		const formattedDate: string = moment(rangeStartDate).add(dayCount, "days").format(dateFormat)
		dateRosterMap[formattedDate] = []
	}

	return dateRosterMap
}

export const checkConflictingShifts = (intervalToCheck: ITimeInterval, datesToCheckOn: string[], dateRosterMap: { [date: string]: IResourceSchedule[] }): IResourceSchedule[] => {
	let currentPresentIntervalsForGivenDates: IResourceSchedule[] = []
	forEach(datesToCheckOn, (date) => (currentPresentIntervalsForGivenDates = currentPresentIntervalsForGivenDates.concat(dateRosterMap[date])))
	return currentPresentIntervalsForGivenDates.filter(({ startTime, endTime, workingDayDate }) => {
		const startEpochOfExisitingShift = getEpochFromDateAndTime(workingDayDate, startTime)
		const endEpochOfExisitingShift = getEpochFromDateAndTime(workingDayDate, endTime)
		const startEpochOfShiftToCheck = getEpochFromDateAndTime(workingDayDate, intervalToCheck.startTime as string)
		const endEpochOfShiftToCheck = getEpochFromDateAndTime(workingDayDate, intervalToCheck.endTime as string)

		if (moment(startEpochOfShiftToCheck).isBefore(moment(endEpochOfExisitingShift)) && moment(endEpochOfShiftToCheck).isAfter(moment(startEpochOfExisitingShift))) return true
		return false
	})
}

export const checkDefaultRosterConflictingShifts = (shiftToCheck: ITimeInterval, daysToCheckOn: string[], groupWiseAllData: any) => {
	let currentPresentIntervalsForGivenDates: any[] = []
	daysToCheckOn.forEach((day) => (currentPresentIntervalsForGivenDates = currentPresentIntervalsForGivenDates.concat(groupWiseAllData[WEEKDAYS[day].value])))

	const conflictingShifts = currentPresentIntervalsForGivenDates.filter(({ startTime, endTime }) => {
		const workingDayDate = moment(new Date()).valueOf()
		const startEpochOfExisitingShift = getEpochFromDateAndTime(workingDayDate, startTime)
		const endEpochOfExisitingShift = getEpochFromDateAndTime(workingDayDate, endTime)
		const startEpochOfShiftToCheck = getEpochFromDateAndTime(workingDayDate, shiftToCheck.startTime as string)
		const endEpochOfShiftToCheck = getEpochFromDateAndTime(workingDayDate, shiftToCheck.endTime as string)

		return moment(startEpochOfShiftToCheck).isBefore(moment(endEpochOfExisitingShift)) && moment(endEpochOfShiftToCheck).isAfter(moment(startEpochOfExisitingShift))
	})
	return conflictingShifts
}

export const getDateFromNumberOfDays = (numOfDays = 14) => {
	return moment(new Date()).add(numOfDays, "days").format("DD MMM, YYYY")
}
